import React from 'react';
import * as Headless from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Button } from '@oysterjs/ui/button';
import {
  type AttachmentGroup,
  type AttachmentGroupMember,
  EMPTY_CUSTOMIZATION_MESSAGE,
  supportsProducerSignature
} from '~/components/workspaces/lib/attachment-group';
import { MENU_PANEL_CLASS } from './artifact-open-pdf-with-attachments-button';
import {
  PdfAttachmentOptions,
  ProducerSignatureOptions,
  type ProducerSignature
} from './pdf-attachment-options';

export const AttachmentCustomizeButton = (props: {
  document: AttachmentGroupMember;
  candidates: AttachmentGroupMember[];
  agencyProducerInformations: ProducerSignature[];
  userProducerInformations: ProducerSignature[];
  initialGroup?: AttachmentGroup;
  disabled?: boolean;
  onSavingChange: (saving: boolean) => void;
  onError: (error: string | null) => void;
  uploadFiles: (files: File[]) => Promise<{ id: string; name: string; size: number }[]>;
  onSave: (members: AttachmentGroupMember[], producerInformationId: string | null) => Promise<void>;
}) => {
  const { document, initialGroup } = props;
  const members = initialGroup?.members ?? [document];
  const anchorIndex = members.findIndex((member) => member.id === document.id);
  const [prefixIds, setPrefixIds] = React.useState(() =>
    members.slice(0, anchorIndex).map((member) => member.id)
  );
  const [suffixIds, setSuffixIds] = React.useState(() =>
    members.slice(anchorIndex + 1).map((member) => member.id)
  );
  const [signatureId, setSignatureId] = React.useState(initialGroup?.producerInformationId ?? null);
  const [localFiles, setLocalFiles] = React.useState(new Map<string, File>());
  const [saving, setSaving] = React.useState(false);
  const [opening, setOpening] = React.useState(0);
  // Retrying a failed render can reuse PDFs already uploaded by this menu.
  const uploaded = React.useRef(new Map<File, AttachmentGroupMember>());
  const candidates = new Map(
    [...props.candidates, ...members].map((member) => [member.id, member])
  );

  const emptyCustomization =
    prefixIds.length === 0 &&
    suffixIds.length === 0 &&
    !(supportsProducerSignature(document) && signatureId);

  const resetForOpen = () => {
    setPrefixIds(members.slice(0, anchorIndex).map((member) => member.id));
    setSuffixIds(members.slice(anchorIndex + 1).map((member) => member.id));
    setSignatureId(initialGroup?.producerInformationId ?? null);
    setLocalFiles(new Map());
    uploaded.current.clear();
    // Reset local file chips and validation without replacing the popover.
    setOpening((value) => value + 1);
  };

  const cancel = (close: () => void) => {
    props.onError(null);
    close();
  };

  const save = async (close: () => void) => {
    if (emptyCustomization) return;
    setSaving(true);
    props.onSavingChange(true);
    props.onError(null);
    try {
      const resolve = async (ids: string[]) => {
        const resolved: AttachmentGroupMember[] = [];
        for (const id of ids) {
          const local = localFiles.get(id);
          if (local && !uploaded.current.has(local)) {
            const [stored] = await props.uploadFiles([local]);
            if (!stored) throw new Error(`Could not upload “${local.name}”. Please try again.`);
            uploaded.current.set(local, { id: stored.id, name: stored.name, kind: 'file' });
          }
          const member = local ? uploaded.current.get(local) : candidates.get(id);
          if (!member) throw new Error('One of these attachments is no longer available.');
          resolved.push(member);
        }
        return resolved;
      };
      const prefix = await resolve(prefixIds);
      const suffix = await resolve(suffixIds);
      await props.onSave([...prefix, document, ...suffix], signatureId);
      close();
    } catch (err) {
      props.onError(err instanceof Error ? err.message : 'Could not customize this attachment.');
    } finally {
      setSaving(false);
      props.onSavingChange(false);
    }
  };

  return (
    <Headless.Popover className="shrink-0">
      {({ close, open }) => (
        <>
          <Headless.PopoverButton
            as={Button}
            plain
            disabled={props.disabled || saving}
            aria-label={`Customize ${document.name}`}
            onClick={() => {
              if (!open) resetForOpen();
            }}
          >
            Customize
            <ChevronDownIcon className="size-4" />
          </Headless.PopoverButton>
          {/* Headless UI caps an anchored panel to the space below the button
              (inline `max-height: min(var(--anchor-max-height,100vh), availableHeight)`),
              so a workspace with several other documents pushes the save controls
              past the fold of the panel's own scroll box — and the nested lists
              inside swallow the wheel before it reaches that box. The panel is a
              flex column instead: one scroll region for the options, the actions
              pinned under it, and anchor padding so it never sits flush against
              the viewport edge.

              The fieldset takes `contents` so its children are the panel's own
              flex items: Chrome lays fieldset content out in an anonymous box
              with an indefinite height, so a `flex-1` child measured 515px of
              content inside a 441px fieldset and pushed the actions out of view. */}
          <Headless.PopoverPanel
            transition
            anchor="bottom end"
            className={clsx(MENU_PANEL_CLASS, 'flex flex-col [--anchor-padding:theme(spacing.4)]')}
            aria-label={`Customize ${document.name}`}
          >
            {opening > 0 && (
              <fieldset key={opening} disabled={saving || props.disabled} className="contents">
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <PdfAttachmentOptions
                    description="Prepend pages before or append pages after the generated document (this attachment only)"
                    dropZoneLabel="Add PDF (this attachment only)"
                    attachmentCatalog={[...candidates.values()]
                      .filter((member) => member.id !== document.id)
                      .map((member) => ({ ...member, mimeType: 'application/pdf' }))}
                    prefixFileIds={prefixIds}
                    suffixFileIds={suffixIds}
                    onChange={(prefix, suffix) => {
                      setPrefixIds(prefix);
                      setSuffixIds(suffix);
                    }}
                    onLocalFilesChange={setLocalFiles}
                  >
                    {supportsProducerSignature(document) && (
                      <ProducerSignatureOptions
                        agencyProducerInformations={props.agencyProducerInformations}
                        userProducerInformations={props.userProducerInformations}
                        value={signatureId}
                        onChange={setSignatureId}
                        description="Choose a signature to include on this attachment."
                      />
                    )}
                  </PdfAttachmentOptions>
                  {/* Only the actions are pinned, so the footer stays one row tall and
                      the panel spends its height on the options. This note scrolls with
                      them; the empty-customization alert does not, because it is the
                      reason the adjacent Save button is disabled. */}
                  <p className="px-6 pb-4 text-xs text-neutral-500 dark:text-neutral-400">
                    Saved as one PDF. Later edits to the source documents won’t change this
                    attachment.
                  </p>
                </div>
                <div className="shrink-0 border-t border-neutral-200 bg-white px-6 py-3 dark:border-white/10 dark:bg-neutral-800">
                  {emptyCustomization && (
                    <p role="alert" className="mb-2 text-xs text-red-600 dark:text-red-500">
                      {EMPTY_CUSTOMIZATION_MESSAGE}
                    </p>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button type="button" color="white" onClick={() => cancel(close)}>
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      color="sky"
                      disabled={emptyCustomization}
                      onClick={() => void save(close)}
                    >
                      {saving ? 'Saving…' : 'Save attachment'}
                    </Button>
                  </div>
                </div>
              </fieldset>
            )}
          </Headless.PopoverPanel>
        </>
      )}
    </Headless.Popover>
  );
};
